import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Check, X } from "lucide-react";

/**
 * Combobox: searchable select that also lets the user CREATE a brand-new value.
 *
 * Props:
 *  - value: current value (string)
 *  - onChange: (newValue: string) => void
 *  - options: array of { value: string, label?: string, hint?: string }
 *  - placeholder: input placeholder
 *  - allowCreate: boolean (default true) – shows "Create new" affordance when text is not in options
 *  - createLabel: text used in the create row, e.g. "Create new category"
 *  - testId: data-testid root
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Pick or type to create…",
  allowCreate = true,
  createLabel = "Create new",
  testId = "combobox",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => `${o.value} ${o.label || ""}`.toLowerCase().includes(q))
    : options;
  const exactMatch = options.some((o) => o.value.toLowerCase() === q);
  const showCreate = allowCreate && q && !exactMatch;

  const pick = (val) => {
    onChange(val);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="w-full input-bhega flex items-center justify-between text-left disabled:opacity-50"
        data-testid={`${testId}-trigger`}
      >
        <span className={value ? "text-foreground" : "text-muted-foreground"}>
          {value || placeholder}
        </span>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-card border border-border rounded-none shadow-lg" data-testid={`${testId}-panel`}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (showCreate) pick(query.trim());
                  else if (filtered[0]) pick(filtered[0].value);
                }
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder="Type to filter or create…"
              className="flex-1 outline-none text-sm"
              data-testid={`${testId}-search`}
            />
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setQuery(""); }}
                className="text-xs text-muted-foreground hover:text-foreground underline"
                data-testid={`${testId}-clear`}
              >
                clear
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto" data-testid={`${testId}-list`}>
            {filtered.length === 0 && !showCreate && (
              <li className="px-3 py-3 text-xs text-muted-foreground">No matches.</li>
            )}
            {filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => pick(o.value)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-foreground/10 flex items-center gap-2 ${value === o.value ? "bg-[#FFBE06]/10 font-semibold" : ""}`}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  {value === o.value && <Check size={12} className="text-brand" />}
                  <span className="flex-1">
                    {o.label || o.value}
                    {o.hint && <span className="ml-2 text-xs text-muted-foreground">{o.hint}</span>}
                  </span>
                </button>
              </li>
            ))}
            {showCreate && (
              <li className="border-t border-border">
                <button
                  type="button"
                  onClick={() => pick(query.trim())}
                  className="w-full text-left px-3 py-2.5 text-sm bg-[#FFBE06]/5 hover:bg-[#FFBE06]/15 flex items-center gap-2 font-semibold"
                  data-testid={`${testId}-create-new`}
                >
                  <Plus size={14} className="text-brand" />
                  {createLabel}: <span className="ml-1 underline">{query.trim()}</span>
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
